import React, { useState } from 'react'
import axios from 'axios'
import { logger } from '@/utils/logger'

interface CellDetection {
  id: string
  cell_class: string
  confidence: number
  bbox: {
    x: number
    y: number
    width: number
    height: number
  }
  is_abnormal: boolean
}

interface AIResult {
  diagnosis_result: string
  diagnosis_confidence: number
  total_cells_detected: number
  abnormal_cells_detected: number
  cells: CellDetection[]
  processing_time: number
}

interface AIDiagnosisPanelProps {
  slideId: string
  onDetectionsLoad: (cells: CellDetection[]) => void
  onCellClick?: (cell: CellDetection) => void
}

export const AIDiagnosisPanel: React.FC<AIDiagnosisPanelProps> = ({
  slideId,
  onDetectionsLoad,
  onCellClick
}) => {
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<AIResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDetections, setShowDetections] = useState(true)
  const [selectedClass, setSelectedClass] = useState<string>('all')

  const startDetection = async () => {
    try {
      setIsRunning(true)
      setProgress(0)
      setError(null)
      setResult(null)

      if (!slideId) {
        setError('Slide ID is missing')
        setIsRunning(false)
        return
      }

      // Call PACS backend API to start AI detection
      const token = localStorage.getItem('token')
      logger.debug('Starting AI detection for slide:', slideId)

      const response = await axios.post(
        `/api/v1/ai/detections/run_detection/`,
        { slide_id: slideId },
        { headers: { Authorization: `Token ${token}` } }
      )

      logger.debug('AI detection response:', response.data)

      // Use detection_id (both for new and existing detections)
      const detectionId = response.data.detection_id || response.data.task_id

      if (!detectionId) {
        setError('Failed to get detection ID from server')
        setIsRunning(false)
        return
      }

      // Poll for status
      pollStatus(detectionId)
    } catch (err: any) {
      console.error('AI detection error:', err)
      setError(err.response?.data?.error || err.message || 'Failed to start AI detection')
      setIsRunning(false)
    }
  }

  const pollStatus = async (id: string) => {
    const token = localStorage.getItem('token')
    const maxAttempts = 300 // 5 minutes max
    let attempts = 0

    const interval = setInterval(async () => {
      try {
        attempts++
        if (attempts > maxAttempts) {
          clearInterval(interval)
          setError('Detection timeout')
          setIsRunning(false)
          return
        }

        const response = await axios.get(
          `/api/v1/ai/detections/${id}/status/`,
          { headers: { Authorization: `Token ${token}` } }
        )

        const { status, progress: currentProgress } = response.data

        setProgress(currentProgress || 0)

        if (status === 'completed') {
          clearInterval(interval)
          loadResult(id)
        } else if (status === 'failed') {
          clearInterval(interval)
          setError(response.data.error_message || 'Detection failed')
          setIsRunning(false)
        }
      } catch (err: any) {
        clearInterval(interval)
        setError('Failed to check status')
        setIsRunning(false)
      }
    }, 1000)
  }

  const loadResult = async (id: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(
        `/api/v1/ai/detections/${id}/`,
        { headers: { Authorization: `Token ${token}` } }
      )

      const aiResult = response.data
      setResult(aiResult)
      setIsRunning(false)
      setProgress(100)

      // Send detections to viewer for overlay
      if (showDetections && aiResult.cells) {
        onDetectionsLoad(aiResult.cells)
      }
    } catch (err: any) {
      setError('Failed to load results')
      setIsRunning(false)
    }
  }

  const toggleDetections = () => {
    const newShowDetections = !showDetections
    setShowDetections(newShowDetections)

    if (newShowDetections && result?.cells) {
      onDetectionsLoad(result.cells)
    } else {
      onDetectionsLoad([])
    }
  }

  const filterCells = (cells: CellDetection[]) => {
    if (selectedClass === 'all') return cells
    if (selectedClass === 'abnormal') return cells.filter(c => c.is_abnormal)
    return cells.filter(c => c.cell_class === selectedClass)
  }

  const getDiagnosisColor = (diagnosis: string) => {
    const colors: Record<string, string> = {
      'NILM': 'text-green-600',
      'ASC-US': 'text-yellow-600',
      'ASC-H': 'text-orange-500',
      'LSIL': 'text-orange-600',
      'HSIL': 'text-red-600',
      'SCC': 'text-red-800',
      'AGC': 'text-purple-600',
      'AIS': 'text-purple-800'
    }
    return colors[diagnosis] || 'text-gray-600'
  }

  const uniqueClasses = result?.cells
    ? Array.from(new Set(result.cells.map(c => c.cell_class)))
    : []

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">AI Diagnosis</h3>
        {result && (
          <button
            onClick={toggleDetections}
            className={`px-3 py-1 text-sm rounded ${
              showDetections
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            {showDetections ? 'Hide' : 'Show'} Detections
          </button>
        )}
      </div>

      {/* Start Button */}
      {!isRunning && !result && (
        <button
          onClick={startDetection}
          disabled={isRunning}
          className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          Run AI Detection
        </button>
      )}

      {/* Progress */}
      {isRunning && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Processing...</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Diagnosis */}
          <div className="bg-gray-50 rounded p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Diagnosis:</span>
              <span className={`text-xl font-bold ${getDiagnosisColor(result.diagnosis_result)}`}>
                {result.diagnosis_result}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Confidence:</span>
              <span className="text-sm font-semibold">
                {(result.diagnosis_confidence * 100).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded p-3">
              <div className="text-2xl font-bold text-blue-900">
                {result.total_cells_detected}
              </div>
              <div className="text-xs text-blue-700">Total Cells</div>
            </div>
            <div className="bg-red-50 rounded p-3">
              <div className="text-2xl font-bold text-red-900">
                {result.abnormal_cells_detected}
              </div>
              <div className="text-xs text-red-700">Abnormal Cells</div>
            </div>
          </div>

          {/* Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by class:
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="all">All Cells ({result.cells.length})</option>
              <option value="abnormal">
                Abnormal Only ({result.abnormal_cells_detected})
              </option>
              {uniqueClasses.map((cls) => (
                <option key={cls} value={cls}>
                  {cls} ({result.cells.filter(c => c.cell_class === cls).length})
                </option>
              ))}
            </select>
          </div>

          {/* Cell List */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filterCells(result.cells).map((cell, idx) => (
              <div
                key={idx}
                onClick={() => onCellClick?.(cell)}
                className={`p-3 rounded border cursor-pointer hover:shadow-md transition-shadow ${
                  cell.is_abnormal
                    ? 'bg-red-50 border-red-200 hover:bg-red-100'
                    : 'bg-green-50 border-green-200 hover:bg-green-100'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-sm">
                      {cell.cell_class.replace('_', ' ').toUpperCase()}
                    </div>
                    <div className="text-xs text-gray-600">
                      Position: ({cell.bbox.x}, {cell.bbox.y})
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">
                      {(cell.confidence * 100).toFixed(1)}%
                    </div>
                    {cell.is_abnormal && (
                      <span className="text-xs text-red-600 font-medium">
                        ABNORMAL
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Processing Time */}
          <div className="text-xs text-gray-500 text-center">
            Processed in {result.processing_time.toFixed(2)}s
          </div>

          {/* Rerun Button */}
          <button
            onClick={startDetection}
            className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 text-sm"
          >
            Run Again
          </button>
        </div>
      )}
    </div>
  )
}
